import React, { useState, useRef, useCallback, useEffect } from 'react';
import { View, Pressable, Share, Alert, Image, Platform } from 'react-native';
import { Text } from '@constants/Themed';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserId, selectUserName, selectUserPosts } from '@reduxLocal/selectors/userSelectors';
import * as Animatable from 'react-native-animatable';
import { FontAwesome } from '@expo/vector-icons';
import * as Linking from 'expo-linking';
import toTitleCase from '@hooks/toTitleCase';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { LinearGradient } from 'expo-linear-gradient';
import apis from '@api/api';
import CommentsFlatList from './CommentsFlatList';
import { ADD_LIKE_TO_POST, DELETE_LIKE_FROM_POST } from '@reduxLocal/actions/types';
import getDaysAgo from '@utils/getDaysAgo';
import { Popover } from 'tamagui';
import useGlobalUser from '@hooks/getGlobalUser';

interface FeedComponentProps {
  item: any;
  index: number;
  type: string;
  forceHorizontal: boolean;
  refresh: () => void
}

const ActiveFeedComponent = ({ item, index, refresh, type, forceHorizontal, pillButtons = false }) => {
  if(!item) return null;
  const post = item;
  const _id = useSelector(selectUserId);
  const { name, surname, fullName } = useSelector(selectUserName);
  const posts = useSelector(selectUserPosts);
  const [likes, setLikes] = useState(item.likes);
  const theme = themeSelector();
  const id = item._id.toString();
  const [fullView, setFullView] = useState(false);
  const [commentView, setCommentView] = useState(false);
  const [replyVisible, setReplyVisible] = useState(false);
  const [replyText, setReplyText] = useState('');
  const likeRef = useRef<Animatable>(null);
  const responseAnimRef = useRef<Animatable>(null);
  const responseAnimRef2 = useRef<Animatable>(null);
  const [aiResultText, setAIResultText] = useState('Tap me to look through the comments! =>');
  const [aiReponseLoading, setAIResponseLoading] = useState(false);
  const [topComment, setTopComment] = useState({ topComment: "", topItem: {} });
  const dispatch = useDispatch();
  const [menuPop, setMenuPop] = useState(null);
  const [showLikes, setShowLikes] = useState(null);
  const user = useGlobalUser();
  const [itemComments, setItemComments] = useState(Array.isArray(item.comments) ? item.comments : []);
  const [postLikes, setPostLikes] = useState(item.likes || []);

  const optionActions = [
    {
      id: 'save',
      label: 'Save',
      helpText: 'Save this post for later',
      onPress: () => { console.log('Save') },
      icon: <FontAwesome style={{ alignSelf: "center" }} name="save" size={20} color={theme ? "black" : "white"} />
    },
    {
      id: 'embed',
      label: 'Embed',
      helpText: 'Embed this post in your website',
      onPress: () => { console.log('Embed') },
      icon: <FontAwesome style={{ alignSelf: "center" }} name="code" size={20} color={theme ? "black" : "white"} />
    },
    {
      id: 'disableNotifications',
      label: 'Disable Notifications',
      helpText: 'Disable notifications for this post',
      onPress: () => { console.log('Disable Notifications') },
      icon: <FontAwesome style={{ alignSelf: "center" }} name="bell-slash" size={20} color={theme ? "black" : "white"} />
    },
    {
      id: "hide",
      label: "Hide",
      helpText: 'Hide this post from your feed',
      onPress: () => { console.log("Hide") },
      icon: <FontAwesome style={{ alignSelf: "center" }} name="eye-slash" size={20} color={theme ? "black" : "white"} />
    },
    {
      id: "report",
      label: "Report",
      helpText: 'Report this post for inappropriate content',
      onPress: () => { console.log("Report") },
      icon: <FontAwesome style={{ alignSelf: "center" }} name="exclamation-triangle" size={20} color={theme ? "black" : "white"} />
    },
    {
      id: 'block',
      label: 'Block',
      helpText: 'Block this user from your feed',
      onPress: () => { console.log('Block') },
      icon: <FontAwesome style={{ alignSelf: "center" }} name="ban" size={20} color={theme ? "black" : "white"} />
    }
  ]

  const [optionActionList, setOptionActionList] = useState(optionActions);

  useEffect(() => { findTopComment() }, [item.comments]);


  const findTopComment = () => {
    // Find comment with the most likes
    if (!item.comments || !Array.isArray(item.comments) || item.comments.length === 0) {
      setTopComment({ topComment: "", topItem: {} });
      return;
    }

    let topComment = '';
    let topItem: any;
    let topLikes = 0;
    item.comments.forEach((comment: any) => {
      if (comment?.likes > topLikes && comment?.comment) {
        topLikes = comment.likes;
        topComment = comment.comment;
        topItem = comment;
      }
    });
    if (topComment) {
      setTopComment({ topComment, topItem });
    } else if (item.comments[0]?.comment) {
      setTopComment({ topComment: item.comments[0].comment, topItem: item.comments[0] });
    } else {
      setTopComment({ topComment: "", topItem: {} });
    }
  };

  const onShare = async () => {
    const url = Linking.createURL(`/feed/${item._id}`);

    try {
      const result = await Share.share(
        {
          message: `${toTitleCase(name)} ${toTitleCase(surname)} found this awesome post! ${url}`,
          url: url,
          title: 'SmartSaaS',
        },
        {
          dialogTitle: 'Share this post',
          subject: `${toTitleCase(name)} ${toTitleCase(surname)} found this awesome post! ${url}`,
        }
      );
      if (result.action === Share.sharedAction) {
        await apis.addShareToPost(item._id);
      } else if (result.action === Share.dismissedAction) {
        // dismissed
      }
    } catch (error: any) {
      Alert.alert(error.message);
    }
  };

  const _handleLike = async () => {
    likeRef.current?.tada?.(400);
    if (posts?.liked_posts?.includes(id)) {
      await apis.removeLikeFromPost(item._id).then(() => {
        setLikes(likes - 1);
        dispatch({ type: DELETE_LIKE_FROM_POST, payload: id });
        return;
      });
    } else {
      await apis.addLikeToPost(item._id).then(() => {
        setLikes(likes + 1);
        dispatch({ type: ADD_LIKE_TO_POST, payload: id });
      });
    }
    refresh?.();
  };

  const _sendReply = async (text: string) => {
    await apis.addCommentToPost(item._id, { comment: text }).then((res) => {
      console.log(res);
      const currentComments = Array.isArray(item.comments) ? item.comments : [];
      setItemComments([...currentComments, res.data]);
      setFullView(true);
      setCommentView(true);
    });
    responseAnimRef2.current?.shake?.(400);
  };

  const _handleComment = useCallback(() => {
    if (!item.comments || item.comments.length === 0) {
      return responseAnimRef2.current?.shake?.(400);
    }
    if (!fullView) {
      setFullView(!fullView);
      setTimeout(() => {
        setCommentView(!commentView);
      }, 300);
    } else {
      setCommentView(!commentView);
      setFullView(!fullView);
    }
  }, [item, commentView, fullView]);

   const _handleRemoveComment = (commentId: string) => {
     const currentComments = Array.isArray(item.comments) ? item.comments : [];
     const newComments = currentComments.filter((comment: any) => comment?._id !== commentId);
     if (newComments.length > 0 && newComments[0]?.comment) {
       setTopComment({ topComment: newComments[0].comment, topItem: newComments[0] });
     } else {
       setTopComment({ topComment: "", topItem: {} });
     }
    setItemComments(newComments);
  }

  useEffect(() => {
    if (item.postedBy?._id === user) {
      const newActions = optionActions.filter((action) => action.id !== 'block');
      // Add delete action to top of list
      newActions.unshift({
        id: 'delete',
        label: 'Delete',
        helpText: 'Delete this post',
        onPress: async () => { await apis.deletePost(item._id).then(() => { setMenuPop(null); refresh?.(); }) },
        icon: <FontAwesome style={{ alignSelf: "center" }} name="trash" size={20} color={theme ? "black" : "white"} />
      });
      setOptionActionList(newActions);
    }
  }, [])

  return (
    <Animatable.View transition={'minHeight'} style={{ alignSelf: "stretch", flex: 1, maxHeight: forceHorizontal ? (!fullView && Platform.OS === 'web' ? 300 : null) : null, minWidth: Platform.OS === 'web' ? "100%" : "95%", overflow: "hidden", borderRadius: 10, minHeight: fullView ? 550 : (item.media && item.media.length > 0 ? (forceHorizontal ? 250 : 350) : "100%"), padding: 10, paddingHorizontal: 0, marginBottom: 0, paddingBottom: 0, borderColor: '#58BFEA', backgroundColor: theme ? 'white' : '#33333390', shadowRadius: 20, shadowOpacity: 0.1 }}>
      <LinearGradient colors={theme ? ['#1E90FF', 'rgb(90,192,234)'] : ['#333333', 'black']} style={{ width: '100%', paddingHorizontal: 10, height: 30, paddingRight: 40, marginHorizontal: -10, top: -15, paddingLeft: Platform.select({ default: undefined, web: '3%', }), left: 5, borderTopLeftRadius: 5, flexDirection: "row", paddingVertical: 3, }}>
        <View style={{ flex: 4, alignSelf: "stretch", minWidth: "75%", flexDirection: "row" }}>
          <Text numberOfLines={1} style={{ flex: 1, fontSize: 12, width: 50, minHeight: 12, fontWeight: '500', textAlign: 'left', color: 'white', overflow: "visible" }}>{toTitleCase(item.postedBy?.profile_information?.name)} {toTitleCase(item.postedBy?.profile_information?.surname)}</Text>
        </View>
        <Text style={{ flex: 1, fontSize: 10, color: "white", textAlign: 'right', paddingHorizontal: 5 }}>{getDaysAgo(item.createdAt)}</Text>
        {
          Platform.OS === 'web' &&
        <Popover
          open={!!menuPop}
          onClose={() => { setMenuPop(null) }}
          anchorEl={menuPop}
          anchorOrigin={{ vertical: 45, horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          style={{ borderRadius: 20 }}
        >
          {
            optionActionList.map((action, index) => {
              const [hovered, setHovered] = useState(false);
              return (
                <Pressable key={index} onPress={action.onPress} onHoverIn={() => setHovered(true)} onHoverOut={() => setHovered(false)} style={[{ backgroundColor: hovered ? '#00000030' : 'transparent', flexDirection: 'row', flex: 1, padding: 10, alignContent: 'center', alignItems: 'center', justifyContent: 'center', alignSelf: 'stretch', gap: 10 }]}>
                  <View style={{ flex: 1, maxWidth: 40 }}>
                    {action.icon}
                  </View>
                  <View style={{ flexDirection: "column", flex: 6 }}>
                    <Text style={{ color: theme ? "black" : "white" }}>{action.label}</Text>
                    <Text style={{ color: theme ? "black" : "white", fontSize: 10 }}>{action.helpText}</Text>
                  </View>
                </Pressable>
              )
            })
          }
        </Popover>
        }
      </LinearGradient>
      <Pressable onPress={(ev) => { setMenuPop(ev.currentTarget) }} style={{ overflow: "hidden", position: 'absolute', right: 0, top: 0, width: 40, borderTopRightRadius: 10, borderColor: 'rgb(90,192,234)' }}>
        <Image source={{ uri: item.postedBy?.profile_information?.avatar.img || "https://storage.googleapis.com/smartsaas-avatar-store/Asset%202.png" }} style={{ overflow: "hidden", top: 0, flex: 1, width: 42, resizeMode: 'contain', height: 42, backgroundColor: theme ? 'white' : 'grey', borderBottomLeftRadius: 20, borderWidth: 2, borderRightWidth: 0, borderTopWidth: 0, borderTopRightRadius: 10, borderColor: 'rgb(90,192,234)', minHeight: 40 }} />
      </Pressable>
      <View style={{ flex: 5, minHeight: "80%", paddingBottom: 60 }}>
        <CommentsFlatList removeCallback={(commentId) => { _handleRemoveComment(commentId); } } feedId={item._id} comments={itemComments} emptyPlaceHolder={"No comments just yet"} />
      </View>
    </Animatable.View>
  )
}

export default ActiveFeedComponent;